/**
 * 스포츠정보 출처 URL 유틸 (#0011006)
 *
 * 저장 전 검증 · 종류 판별 · APK 브릿지용 주소와 패키지명을 다룬다.
 * **youtube 와 X 의 차이는 전부 `SOURCE_VENDORS` 표 한 곳에 있다**
 */

export const SOURCE_URL_MAX_LENGTH = 600

/**
 * 출처 URL 로 허용하는 형식 — `http://…` 또는 `https://…` 이고 중간에 공백이 없어야 한다
 *
 *   https://x.com/a/status/1  → 통과
 *   foo https://x.com         → 거부 (앞에 딴 글자)
 *   javascript:alert(1)       → 거부 (http/https 아님)
 *
 * admin-front 의 `Util.checkUrl` 은 `^…$` 앵커가 없어 "foo http://x" 도 통과한다.
 * 서버 신뢰선이 그 허점을 물려받으면 안 되므로 앵커(`^` `$`)를 명시한다.
 */
const SOURCE_URL_PATTERN = /^https?:\/\/[^\s]+$/i

export const SOURCE_URL_KIND = {
  YOUTUBE: 'youtube',
  X: 'x',
  GENERAL: 'general',
} as const

// 위 객체의 값들만 모은 타입 — 즉 'youtube' | 'x' | 'general'
type SourceUrlKind = typeof SOURCE_URL_KIND[keyof typeof SOURCE_URL_KIND]

/**
 * 저장 전 정규화 — 값이 없으면 `null`
 * - FormData 경로에서 문자열 'null' 이 흘러들 수 있어 여기서 걸러낸다
 */
export function normalizeSourceUrl(raw: unknown): string | null {
  const value = `${raw ?? ''}`.trim()
  if (value.length === 0 || value === 'null' || value === 'undefined') {
    return null
  }
  return value
}

/**
 * 출처 URL 형식 검증
 * - http/https 만 허용한다. javascript: 가 저장되면 어드민 목록·v2-front 에서 그대로 링크로 렌더된다
 */
export function isValidSourceUrl(url: string): boolean {
  const value = normalizeSourceUrl(url)
  if (value === null) {
    return false
  }
  if (value.length > SOURCE_URL_MAX_LENGTH) {
    return false
  }
  return SOURCE_URL_PATTERN.test(value)
}

/**
 * URL 에서 호스트만 뽑는다. 못 뽑으면 빈 문자열
 *
 *   https://www.youtube.com/watch?v=abc  → 'www.youtube.com'
 *   https://user@X.com:443/a             → 'x.com'   (로그인정보·포트 제거 + 소문자)
 *   그냥문자열                            → ''
 *
 * social-network-detector.uriParser 는 `import urlParser from 'url'` 이라
 * esModuleInterop 이 꺼진 컴파일 설정에서 undefined 가 된다. 의존하지 않는다
 */
function extractHost(url: string): string {
  const matched = /^https?:\/\/([^/?#]+)/i.exec(url)
  if (matched === null) {
    return ''
  }
  const authority = matched[1]
  const withoutAuth = authority.includes('@')
    ? authority.slice(authority.lastIndexOf('@') + 1)
    : authority
  return withoutAuth.replace(/:\d+$/, '').toLowerCase()
}

/**
 * 호스트가 그 도메인(또는 서브도메인)인가
 *
 *   isHostOf('youtube.com', 'youtube.com')    → true
 *   isHostOf('m.youtube.com', 'youtube.com')  → true   (서브도메인)
 *   isHostOf('myx.com', 'x.com')              → false  (그냥 뒤에 붙은 것뿐)
 *
 * `extractHost` 가 이미 소문자로 만들어 주므로 대소문자는 신경 쓰지 않는다
 */
function isHostOf(host: string, domain: string): boolean {
  return host === domain || host.endsWith(`.${domain}`)
}

/**
 * 출처 URL 벤더 한 종류의 정의 — youtube 와 X 의 "다른 점"을 담는 그릇이다
 */
interface SourceVendor {
  /** 이 벤더로 판별됐을 때의 종류 값 */
  kind: SourceUrlKind
  /** 이 벤더로 볼 도메인. 서브도메인도 포함된다 (`isHostOf` 참조) */
  domains: string[]
  /** APK 브릿지가 `setPackage` 에 쓸 안드로이드 패키지명 */
  androidPackage: string
  /** APK 로 넘기기 전 호스트를 바꿔야 하는 벤더만 지정한다. 없으면 그대로 넘긴다 */
  appLinkRewrite?: { fromDomains: string[]; toHost: string }
}

const X_ANDROID_PACKAGE = 'com.twitter.android'

/**
 * 벤더 표 — youtube 와 X 의 차이는 전부 여기 있다
 *
 * social-network-detector 의 x 라벨에는 x.com 이 없어 그대로 쓸 수 없다.
 * 공유 파일을 고치면 jsxutils 의 판정까지 바뀌므로 출처 URL 전용으로 둔다.
 */
const SOURCE_VENDORS: SourceVendor[] = [
  {
    kind: SOURCE_URL_KIND.YOUTUBE,
    domains: ['youtube.com', 'youtu.be', 'youtube-nocookie.com'],
    androidPackage: 'com.google.android.youtube',
  },
  {
    kind: SOURCE_URL_KIND.X,
    domains: ['x.com', 'twitter.com', 't.co'],
    androidPackage: X_ANDROID_PACKAGE,
    // 실기에서 twitter.com 으로 넘겼을 때 게시물이 열렸다. x.com 은 확인하지 않았다
    appLinkRewrite: { fromDomains: ['x.com'], toHost: 'twitter.com' },
  },
]

/**
 * 정규화된 URL 이 어느 벤더인지 찾는다 — 못 찾으면 null (= 일반 URL)
 */
function findVendor(url: string): SourceVendor | null {
  const host = extractHost(url)
  if (host.length === 0) {
    return null
  }
  return SOURCE_VENDORS.find(vendor => vendor.domains.some(domain => isHostOf(host, domain))) ?? null
}

/**
 * 출처 URL 종류 판별 — 인게임 MO 분기에서 임베드/팝업을 고르는 데 쓴다
 */
export function detectSourceUrlKind(url: string): SourceUrlKind {
  const value = normalizeSourceUrl(url)
  if (value === null) {
    return SOURCE_URL_KIND.GENERAL
  }

  const vendor = findVendor(value)
  return vendor === null ? SOURCE_URL_KIND.GENERAL : vendor.kind
}

/**
 * APK 브릿지로 넘기기 전 호스트를 앱이 받아주는 쪽으로 바꾼다 (#0011006)
 *
 *   https://x.com/누구/status/123 → https://twitter.com/누구/status/123 (youtube 는 그대로)
 *
 * 대상이 아니면 원본을 그대로 돌려준다
 */
export function toAppLinkFriendlyUrl(url: string): string {
  const value = normalizeSourceUrl(url)
  if (value === null) {
    return url
  }

  const vendor = findVendor(value)
  const rewrite = vendor === null ? undefined : vendor.appLinkRewrite
  if (rewrite === undefined) {
    return value
  }

  const host = extractHost(value)
  if (!rewrite.fromDomains.some(domain => isHostOf(host, domain))) {
    return value
  }
  // 'https://x.com' 부분만 'https://twitter.com' 으로 바꾼다. 경로·쿼리는 건드리지 않는다
  return value.replace(/^(https?:\/\/)[^/?#]+/i, `$1${rewrite.toHost}`)
}

/**
 * 출처 URL 을 열어 줄 안드로이드 앱 패키지명 (#0011006)
 * - APK 브릿지가 `setPackage` 에 쓴다. App Links 검증 상태와 무관하게 앱을 직접 지정하기 위한 것
 * - 대상이 아니면 빈 문자열 — 브릿지가 브라우저로 폴백한다
 */
export function getSourceAppPackageName(url: string): string {
  const value = normalizeSourceUrl(url)
  if (value === null) {
    return ''
  }

  const vendor = findVendor(value)
  return vendor === null ? '' : vendor.androidPackage
}
